<template>
  <institutelayout>
    <div class="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div class="container mx-auto max-w-6xl">
        
        <!-- Form Section - Full Width -->
        <div class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6">
          <div class="bg-blue-500 px-8 py-4">
            <h2 class="text-xl font-semibold text-white">Carousel Configuration</h2>
          </div>
          
          <div class="p-6">
            <form @submit.prevent="saveChanges" class="space-y-6">
              <!-- Background Image and Content Section - Side by Side -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <!-- Background Image Section -->
                <div class="bg-gray-50 rounded-xl p-4">
                  <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <svg class="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                    Background Image
                  </h3>
                  
                  <div class="relative">
                    <input 
                      type="file" 
                      id="backgroundImage" 
                      @change="handleBackgroundUpload" 
                      accept="image/*"
                      class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors duration-200 bg-white">
                      <svg class="w-10 h-10 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                      </svg>
                      <p class="text-gray-600 font-medium text-sm">
                        {{ form.backgroundImageName || 'Click to upload background image' }}
                      </p>
                      <p class="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</p>
                    </div>
                  </div>
                </div>
  
                <!-- Content Section -->
                <div class="bg-gray-50 rounded-xl p-4">
                  <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <svg class="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                    </svg>
                    Content Settings
                  </h3>
                  
                  <div class="space-y-4">
                    <div>
                      <label for="title" class="block text-sm font-semibold text-gray-700 mb-2">
                        Carousel Title <span class="text-red-500">*</span>
                      </label>
                      <input 
                        type="text" 
                        id="title" 
                        v-model="form.title" 
                        required 
                        class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                        placeholder="Enter carousel title"
                      />
                    </div>
  
                    <div>
                      <label for="description" class="block text-sm font-semibold text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea 
                        id="description" 
                        v-model="form.description" 
                        rows="3"
                        class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm resize-none"
                        placeholder="Enter carousel description"
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
  
              <!-- Call to Action Buttons Section -->
              <div class="bg-gray-50 rounded-xl p-4">
                <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"></path>
                  </svg>
                  Call to Action Buttons
                </h3>
                
                <div class="space-y-3 max-h-64 overflow-y-auto">
                  <div v-for="(button, index) in form.ctaButtons" :key="index" class="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                    <div class="flex items-center space-x-3">
                      <div class="flex-1">
                        <input 
                          type="text" 
                          v-model="button.text" 
                          placeholder="Button Text" 
                          class="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all duration-200"
                        />
                      </div>
                      <div class="flex-1">
                        <input 
                          type="url" 
                          v-model="button.url" 
                          placeholder="https://example.com" 
                          class="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all duration-200"
                        />
                      </div>
                      <div class="w-20">
                        <input 
                          type="color" 
                          v-model="button.color" 
                          class="w-full h-8 border border-gray-200 rounded-md cursor-pointer"
                          title="Button Color"
                        />
                      </div>
                      <button 
                        type="button" 
                        @click="removeCtaButton(index)" 
                        class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors duration-200"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <button 
                    type="button" 
                    @click="addCtaButton" 
                    class="w-full py-2 px-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-400 hover:text-purple-600 transition-colors duration-200 flex items-center justify-center space-x-2 text-sm"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    <span>Add CTA Button</span>
                  </button>
                </div>
              </div>
  
              <!-- Save Button -->
              <div class="flex justify-end pt-4 border-t border-gray-200">
                <button 
                type="submit" 
                class="px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <span class="flex items-center space-x-2">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                  <span>Save Changes</span>
                </span>
              </button>
              </div>
            </form>
          </div>
        </div>
  
        <!-- Preview Section - Below Form -->
        <div class="bg-white rounded-2xl shadow-xl border border-gray-100 hidden md:flex flex-col overflow-hidden">
          <div class="bg-blue-500 px-8 py-4">
            <h2 class="text-xl font-semibold text-white">Live Preview</h2>
          </div>
          
          <div class="p-6">
            <!-- Preview Carousel -->
            <div class="border border-gray-200 rounded-xl overflow-hidden shadow-lg">
              <div 
                class="relative min-h-[500px] bg-cover bg-center bg-no-repeat flex items-center"
                :style="{
                  backgroundImage: form.backgroundImageUrl ? `url(${form.backgroundImageUrl})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontFamily: form.fontFamily
                }"
              >
                <!-- Overlay -->
                <div 
                  class="absolute inset-0 bg-blue-400"
                  :style="{ opacity: form.overlayOpacity }"
                ></div>
  
                <!-- Content -->
                <div 
                  class="relative z-10 w-full px-8 py-12"
                  :class="{
                    'text-left': form.contentPosition === 'left',
                    'text-center': form.contentPosition === 'center',
                    'text-right': form.contentPosition === 'right'
                  }"
                >
                  <div 
                    class="max-w-4xl"
                    :class="{
                      'mx-0': form.contentPosition === 'left',
                      'mx-auto': form.contentPosition === 'center',
                      'ml-auto': form.contentPosition === 'right'
                    }"
                  >
                    <!-- Title -->
                    <h1 
                      :class="form.titleSize"
                      class="font-bold mb-6 drop-shadow-lg"
                      :style="{ 
                        color: form.titleColor,
                        fontFamily: form.fontFamily
                      }"
                    >
                      {{ form.title || 'Your Carousel' }}
                    </h1>
                    
                    <!-- Description -->
                    <p 
                      v-if="form.description"
                      :class="form.descriptionSize"
                      class="mb-8 drop-shadow-md leading-relaxed"
                      :style="{ 
                        color: form.descriptionColor,
                        fontFamily: form.fontFamily
                      }"
                    >
                      {{ form.description }}
                    </p>
  
                    <!-- CTA Buttons -->
                    <div 
                      v-if="form.ctaButtons.length > 0"
                      class="flex flex-wrap gap-4"
                      :class="{
                        'justify-start': form.contentPosition === 'left',
                        'justify-center': form.contentPosition === 'center',
                        'justify-end': form.contentPosition === 'right'
                      }"
                    >
                      <button 
                        v-for="(button, index) in form.ctaButtons" 
                        :key="index"
                        class="px-6 py-3 font-semibold rounded-lg hover:opacity-90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                        :style="{ 
                          backgroundColor: button.color || '#6366f1',
                          color: getContrastColor(button.color || '#6366f1'),
                          fontFamily: form.fontFamily
                        }"
                      >
                        {{ button.text || `Button ${index + 1}` }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
  
            <!-- Action Buttons -->
            <div class="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
              <div class="text-sm text-gray-500">
                Preview updates in real-time as you make changes
              </div>
              <div class="flex space-x-3">
                <button 
                  @click="deleteCarousel"
                  class="px-4 py-2 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200 transition-colors duration-200 flex items-center space-x-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </institutelayout>
  </template>
  
  <script setup>
  import { ref } from 'vue';
import Institutelayout from '../institutelayout.vue';
  
  const form = ref({
    backgroundImage: null,
    backgroundImageName: '',
    backgroundImageUrl: '',
    title: '',
    description: '',
    titleColor: '#ffffff',
    descriptionColor: '#f3f4f6',
    fontFamily: 'sans-serif',
    titleSize: 'text-4xl',
    descriptionSize: 'text-lg',
    contentPosition: 'center',
    overlayOpacity: '0.4',
    ctaButtons: [],
  });
  
  const handleBackgroundUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      form.value.backgroundImage = file;
      form.value.backgroundImageName = file.name;
      
      // Create preview URL for the background image
      const reader = new FileReader();
      reader.onload = (e) => {
        form.value.backgroundImageUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      form.value.backgroundImage = null;
      form.value.backgroundImageName = '';
      form.value.backgroundImageUrl = '';
    }
  };
  
  const addCtaButton = () => {
    form.value.ctaButtons.push({ 
      text: '', 
      url: '', 
      color: '#6366f1'
    });
  };
  
  const removeCtaButton = (index) => {
    form.value.ctaButtons.splice(index, 1);
  };
  
  const getContrastColor = (hexColor) => {
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black or white based on luminance
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };
  
  const saveChanges = () => {
    console.log('Carousel Data:', {
      ...form.value,
      backgroundImage: form.value.backgroundImage ? form.value.backgroundImage.name : null,
    });
    alert('Changes saved successfully! Check console for the submitted data.');
  };
  
  const editCarousel = () => {
    alert('Edit functionality - This would allow you to modify the current carousel configuration.');
  };
  
  const deleteCarousel = () => {
    if (confirm('Are you sure you want to delete this carousel? This action cannot be undone.')) {
      // Reset form
      form.value = {
        backgroundImage: null,
        backgroundImageName: '',
        backgroundImageUrl: '',
        title: '',
        description: '',
        titleColor: '#ffffff',
        descriptionColor: '#f3f4f6',
        fontFamily: 'sans-serif',
        titleSize: 'text-4xl',
        descriptionSize: 'text-lg',
        contentPosition: 'center',
        overlayOpacity: '0.4',
        ctaButtons: [],
      };
      alert('Carousel deleted successfully!');
    }
  };
  </script>